/**
 * Snooze Tagging System
 *
 * Parallel to the follow-up tagging system (`./follow-up-tagging`). Lets a user
 * silence the "needs follow-up" reminder for a deal across one or more calendar
 * weeks without touching its follow-up state.
 *
 * Tag Format: "snoozed-{YYYY-W##}" (e.g., "snoozed-2026-W15").
 *
 * A user picks a calendar date range; every ISO week that range touches gets one
 * `snoozed-{week}` tag. The reminder selector skips a deal when the CURRENT week
 * has a snooze tag (`isSnoozedThisWeek`). Tags for past weeks naturally stop
 * mattering once the week rolls over — same self-expiring model as follow-up tags.
 *
 * Snooze tags live in the same `deals.tags` text[] array as follow-up tags; the
 * `snoozed-` / `followed-up-` prefixes keep the two namespaces independent.
 */

import { getISOWeekString, getWeekStringsInRange } from "./iso-week";

/** Prefix that marks a tag as a snooze tag. */
export const SNOOZE_TAG_PREFIX = "snoozed-";

/** Build the snooze tag for a given week (defaults to current week). */
export function getSnoozeTag(date: Date = new Date()): string {
  return `${SNOOZE_TAG_PREFIX}${getISOWeekString(date)}`;
}

/** Snooze tag for the current week. */
export function getCurrentWeekSnoozeTag(): string {
  return getSnoozeTag();
}

/**
 * Is this deal snoozed for the current week?
 * This is the check the reminder selector uses to skip a deal.
 */
export function isSnoozedThisWeek(tags?: string[]): boolean {
  if (!tags || tags.length === 0) return false;
  return tags.includes(getCurrentWeekSnoozeTag());
}

/**
 * Add a snooze tag for every week the given calendar range touches.
 * Idempotent — weeks already snoozed are left as-is. Returns the updated tags.
 */
export function addSnoozeTagsForRange(tags: string[] | undefined, from: Date, to: Date): string[] {
  const current = tags ?? [];
  const existing = new Set(current);
  const additions = getWeekStringsInRange(from, to)
    .map((week) => `${SNOOZE_TAG_PREFIX}${week}`)
    .filter((tag) => !existing.has(tag));

  return additions.length === 0 ? current : [...current, ...additions];
}

/**
 * Remove the snooze tags for every week the given calendar range touches.
 * Returns the updated tags.
 */
export function removeSnoozeTagsForRange(
  tags: string[] | undefined,
  from: Date,
  to: Date,
): string[] {
  if (!tags || tags.length === 0) return [];
  const toRemove = new Set(getWeekStringsInRange(from, to).map((week) => `${SNOOZE_TAG_PREFIX}${week}`));
  return tags.filter((tag) => !toRemove.has(tag));
}

/** Remove ALL snooze tags (full un-snooze across every week). Returns updated tags. */
export function removeAllSnoozeTags(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags.filter((tag) => !tag.startsWith(SNOOZE_TAG_PREFIX));
}

/** All snooze tags currently on the deal, across every week. */
export function getSnoozeTags(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags.filter((tag) => tag.startsWith(SNOOZE_TAG_PREFIX));
}

/** All non-snooze tags (everything else, including follow-up tags). */
export function getNonSnoozeTags(tags?: string[]): string[] {
  if (!tags || tags.length === 0) return [];
  return tags.filter((tag) => !tag.startsWith(SNOOZE_TAG_PREFIX));
}

/**
 * Extract the `YYYY-W##` week strings from a deal's snooze tags, sorted
 * chronologically (lexical sort works because the format is zero-padded and
 * year-prefixed). Useful for rendering "Snoozed through W25" in the UI.
 */
export function getSnoozedWeeks(tags?: string[]): string[] {
  return getSnoozeTags(tags)
    .map((tag) => tag.slice(SNOOZE_TAG_PREFIX.length))
    .sort();
}

/**
 * Is this deal snoozed for any week at or after the current week?
 * (Past-only snooze tags are treated as inactive.)
 */
export function hasActiveSnooze(tags?: string[]): boolean {
  const currentWeek = getISOWeekString();
  return getSnoozedWeeks(tags).some((week) => week >= currentWeek);
}
